import React, { useState, useEffect, useRef } from "react";
import style from "./DropDown.module.scss";
import CaretDown from "../../assets/svg/icons/common/CaretDown";

/**
 * DropDown component that displays a button with a dropdown menu
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to display in the dropdown button
 * @param {string} [props.bgColor] - Background color of the dropdown button
 * @param {Function} [props.onClick] - Click handler for the dropdown button
 * @param {string} [props.textColor="#000"] - Text color of the dropdown button
 * @param {Array<{text: string, onClick: Function, type?: string, hidden?: boolean}>} [props.options] - Array of options to display in the dropdown
 * @param {string} [props.size="medium"] - Size of the dropdown ("medium" or "small")
 * @param {string} [props.direction="down"] - Direction of the dropdown ("up" or "down")
 * @returns {JSX.Element} DropDown component
 */
function DropDown({
  children,
  bgColor,
  onClick,
  textColor = "#000",
  options,
  size = "medium",
  direction = "down",
  closeOnSelect = true,
  showArrow = false,
  smallFont = false,
}) {
  const [dropOpen, setDropOpen] = useState(false);
  const dropDownRef = useRef(null);

  /**
   * Effect to handle click outside the dropdown
   * Closes the dropdown when clicking outside its container
   */
  useEffect(() => {
    /**
     * Handles click events outside the dropdown
     * @param {MouseEvent} event - The click event
     */
    function handleClickOutside(event) {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setDropOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionClick = (option) => {
    if (closeOnSelect) {
      setDropOpen(false);
    }
    option?.onClick();
  };

  return (
    <div
      className={`${style.drop_down} ${dropOpen ? style.open : ""} ${direction === "up" ? style.drop_up : ""} ${style.drop_down} ${showArrow ? style.dropDownWithArrow : ""} ${smallFont ? style.smallFont : ""}`}
      ref={dropDownRef}
    >
      <button
        className={size == "medium" ? style.button : style.button_small}
        style={{
          backgroundColor: bgColor,
          color: textColor,
        }}
        onClick={() => setDropOpen(!dropOpen)}
      >
        {children}

        {showArrow && (
          <div className={`${style.caret} ${dropOpen && style.open}`}>
            <CaretDown />
          </div>
        )}
      </button>
      {dropOpen && (
        <span
          className={`${style.drop_content} ${size == "medium" ? "" : style.drop_content_small}`}
        >
          {options?.map((option) => (
            <React.Fragment key={option.text}>
              {!option?.hidden && (
                <p
                  key={option.text}
                  onClick={() => handleOptionClick(option)}
                  className={option.type == "danger" ? style.danger : ""}
                >
                  {option.text}
                </p>
              )}
            </React.Fragment>
          ))}
        </span>
      )}
    </div>
  );
}

export default DropDown;
